import { useState } from "react";
import { X, Info, ChevronDown } from "lucide-react";
import {
  SDL_MATURITY_LEVELS,
  SDL_NOT_APPLICABLE_BADGE_CLASS,
  SDL_NOT_APPLICABLE_LABEL,
} from "../constants/sdlMaturity";

interface SDLScoringGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SDLScoringGuideModal({
  isOpen,
  onClose,
}: SDLScoringGuideModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              How Scoring Works
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Scoring Scale */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Scoring Scale (0–4)
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Each question is scored on a 5-point maturity scale, or marked
              N/A:
            </p>
            <div className="space-y-1.5">
              {[
                ...SDL_MATURITY_LEVELS.map((level) => ({
                  score: String(level.score),
                  label: level.label,
                  color: level.badgeClass,
                })),
                {
                  score: "N/A",
                  label: SDL_NOT_APPLICABLE_LABEL,
                  color: SDL_NOT_APPLICABLE_BADGE_CLASS,
                },
              ].map((item) => (
                <div key={item.score} className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${item.color}`}
                  >
                    {item.score}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-2.5 p-2.5 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 rounded-md">
              <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">
                <strong>N/A is not a free pass.</strong> For every control you
                scope out, you must provide a written justification based on
                your threat model or product security context. N/A questions are
                completely excluded from the maturity score calculation.
              </p>
            </div>
          </div>

          {/* How Score is Calculated */}
          <details className="group">
            <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 select-none">
              <ChevronDown className="h-4 w-4 text-gray-400 group-open:rotate-180 transition-transform" />
              How the Maturity Score is Calculated
            </summary>
            <div className="mt-2 pl-5.5 text-xs text-gray-600 dark:text-gray-400 space-y-2">
              <p>
                The overall maturity score is the <strong>average</strong> of
                all answered questions' scores, rounded to two decimal places.
              </p>
              <p>
                Only questions with a numeric score (0–4) are included.{" "}
                <strong>
                  Unanswered questions and N/A questions are completely excluded
                </strong>{" "}
                from the calculation.
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 font-mono text-[11px]">
                Overall Score = Σ(scored question values) ÷ number of scored
                questions (excluding N/A)
              </div>
            </div>
          </details>

          {/* Weight Distribution */}
          <details className="group">
            <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 select-none">
              <ChevronDown className="h-4 w-4 text-gray-400 group-open:rotate-180 transition-transform" />
              Practice Domains &amp; Weight Distribution
            </summary>
            <div className="mt-2 pl-5.5 text-xs text-gray-600 dark:text-gray-400 space-y-2">
              <p>
                The 25 questions span 7 practice domains. Each domain's score is
                the average of its questions. All domains are weighted equally:
              </p>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {[
                  "Security Requirements",
                  "Secure Design",
                  "Secure Implementation",
                  "Security Testing",
                  "Security Deployment",
                  "Security Operations",
                  "Security Governance",
                ].map((practice) => (
                  <div
                    key={practice}
                    className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                    <span className="text-[11px] text-blue-800 dark:text-blue-300">
                      {practice}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </details>

          {/* What each score means */}
          <details className="group" open>
            <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 select-none">
              <ChevronDown className="h-4 w-4 text-gray-400 group-open:rotate-180 transition-transform" />
              What Each Score Means
            </summary>
            <div className="mt-2 pl-5.5 space-y-2">
              {SDL_MATURITY_LEVELS.map((level) => (
                <div
                  key={level.score}
                  className={`border-l-4 rounded-r-md px-3 py-2 ${level.cardClass}`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                      {level.score}
                    </span>
                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      — {level.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-600 dark:text-gray-400">
                    {level.description}
                  </p>
                </div>
              ))}
            </div>
          </details>

          {/* Recommendations */}
          <details className="group">
            <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 select-none">
              <ChevronDown className="h-4 w-4 text-gray-400 group-open:rotate-180 transition-transform" />
              How Recommendations are Derived
            </summary>
            <div className="mt-2 pl-5.5 text-xs text-gray-600 dark:text-gray-400 space-y-2">
              <p>
                After submission, the system identifies the{" "}
                <strong>lowest-scoring practice domain</strong> and generates a
                targeted improvement recommendation.
              </p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>
                  Domains scoring <strong>≤ 2.0</strong> are flagged as "needs
                  improvement"
                </li>
                <li>
                  Domains scoring <strong>≤ 3.0</strong> are flagged for
                  "strengthening"
                </li>
                <li>Focus on the weakest domain first for maximum impact</li>
              </ul>
            </div>
          </details>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-3">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

/** Compact info button that opens the scoring guide modal */
export function SDLScoringGuideButton() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 transition-colors"
        title="How Scoring Works"
      >
        <Info className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Scoring Guide</span>
      </button>
      <SDLScoringGuideModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
